<template>
  <van-overlay :show="showModel" @click="onOverlayClick" :z-index="zIndex">
    <div
      class="viewer-wrapper"
      ref="wrapperRef"
      @click.stop
      @wheel.prevent="onWheel"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @touchstart.passive="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onTouchEnd"
      @mousedown="onMouseDown"
    >
      <img
        :src="src"
        class="viewer-image"
        ref="imgRef"
        :style="{
          transform: 'translate(' + translateX + 'px,' + translateY + 'px) scale(' + scale + ')',
          transition: transitionStyle,
        }"
      />
      <div class="viewer-toolbar">
        <van-icon name="cross" color="#fff" size="25" class="viewer-close" @click="close" />
      </div>
      <div class="wm-overlay-in-viewer" :style="wmStyle"></div>
    </div>
  </van-overlay>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'

const showModel = defineModel<boolean>('show')

const props = defineProps<{
  src: string
  zIndex?: number
  rubberFactor?: number
  minScale?: number
  maxScale?: number
  overlayClose?: boolean
  watermarkText?: string
}>()

const zIndex = computed(() => props.zIndex ?? 2000)
const rubberFactor = computed(() => props.rubberFactor ?? 0.6)
const minScale = computed(() => props.minScale ?? 0.5)
const maxScale = computed(() => props.maxScale ?? 4)
const overlayClose = computed(() => props.overlayClose ?? true)
const wmStyle = computed(() => {
  const text = String(props.watermarkText || '')
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='260'><text x='30%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='rgba(0,0,0,0.12)' font-size='12' font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' transform='rotate(-30,130,130)'>${text}</text></svg>`
  const url = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
  return { backgroundImage: `url("${url}")` }
})

const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const transitioning = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)
const imgRef = ref<HTMLImageElement | null>(null)
const transitionStyle = computed(() => (transitioning.value ? 'transform 0.2s ease-out' : 'none'))
const dragging = ref(false)
const lastX = ref(0)
const lastY = ref(0)
const pinchStartDistance = ref(0)
const pinchStartScale = ref(1)

watch(showModel, (val) => {
  if (val) {
    scale.value = 1
    translateX.value = 0
    translateY.value = 0
    transitioning.value = false
    nextTick(() => {
      void imgRef.value?.clientWidth
    })
  }
})

const onOverlayClick = () => {
  if (overlayClose.value) showModel.value = false
}
const close = () => {
  showModel.value = false
}

const onMouseDown = (e: MouseEvent) => {
  dragging.value = true
  lastX.value = e.clientX
  lastY.value = e.clientY
}
const getLimits = () => {
  const w = wrapperRef.value?.clientWidth || 0
  const h = wrapperRef.value?.clientHeight || 0
  const bw = imgRef.value?.clientWidth || 0
  const bh = imgRef.value?.clientHeight || 0
  const sw = bw * scale.value
  const sh = bh * scale.value
  const limitX = sw > w ? (sw - w) / 2 : 0
  const limitY = sh > h ? (sh - h) / 2 : 0
  return { limitX, limitY }
}
const applyRubber = (val: number, limit: number) => {
  if (limit <= 0) return 0
  const abs = Math.abs(val)
  if (abs <= limit) return val
  const sign = Math.sign(val) || 1
  const exceed = abs - limit
  const factor = rubberFactor.value
  return sign * (limit + exceed * factor)
}
const onMouseMove = (e: MouseEvent) => {
  if (!dragging.value) return
  const dx = e.clientX - lastX.value
  const dy = e.clientY - lastY.value
  const { limitX, limitY } = getLimits()
  const nextX = translateX.value + dx
  const nextY = translateY.value + dy
  translateX.value = applyRubber(nextX, limitX)
  translateY.value = applyRubber(nextY, limitY)
  lastX.value = e.clientX
  lastY.value = e.clientY
}
const onMouseUp = () => {
  dragging.value = false
  const { limitX, limitY } = getLimits()
  const targetX = Math.max(-limitX, Math.min(limitX, translateX.value))
  const targetY = Math.max(-limitY, Math.min(limitY, translateY.value))
  if (targetX !== translateX.value || targetY !== translateY.value) {
    transitioning.value = true
    translateX.value = targetX
    translateY.value = targetY
    window.setTimeout(() => {
      transitioning.value = false
    }, 200)
  }
}
const getTouchDistance = (e: TouchEvent) => {
  const t1 = e.touches[0] || e.changedTouches[0]
  const t2 = e.touches[1] || e.changedTouches[1]
  if (!t1 || !t2) return 0
  const dx = t2.clientX - t1.clientX
  const dy = t2.clientY - t1.clientY
  return Math.hypot(dx, dy)
}
const onTouchStart = (e: TouchEvent) => {
  if ((e.touches && e.touches.length >= 2) || (e.changedTouches && e.changedTouches.length >= 2)) {
    pinchStartDistance.value = getTouchDistance(e)
    pinchStartScale.value = scale.value
    dragging.value = false
  } else {
    const t = e.touches[0] || e.changedTouches[0]
    if (!t) return
    dragging.value = true
    lastX.value = t.clientX
    lastY.value = t.clientY
  }
}
const zoomAt = (anchorX: number, anchorY: number, next: number) => {
  const rect = wrapperRef.value?.getBoundingClientRect()
  if (!rect) {
    scale.value = next
    return
  }
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const r = next / scale.value
  const vx = anchorX - cx
  const vy = anchorY - cy
  translateX.value += vx * (1 - r)
  translateY.value += vy * (1 - r)
  scale.value = next
  const { limitX, limitY } = getLimits()
  translateX.value = applyRubber(translateX.value, limitX)
  translateY.value = applyRubber(translateY.value, limitY)
}
const onTouchMove = (e: TouchEvent) => {
  if ((e.touches && e.touches.length >= 2) || (e.changedTouches && e.changedTouches.length >= 2)) {
    const d = getTouchDistance(e)
    if (d > 0 && pinchStartDistance.value > 0) {
      const next = Math.max(
        minScale.value,
        Math.min(maxScale.value, pinchStartScale.value * (d / pinchStartDistance.value)),
      )
      const t1 = e.touches[0] || e.changedTouches[0]
      const t2 = e.touches[1] || e.changedTouches[1]
      if (t1 && t2) {
        const ax = (t1.clientX + t2.clientX) / 2
        const ay = (t1.clientY + t2.clientY) / 2
        zoomAt(ax, ay, next)
      } else {
        scale.value = next
      }
    }
    return
  }
  if (!dragging.value) return
  const t = e.touches[0] || e.changedTouches[0]
  if (!t) return
  const dx = t.clientX - lastX.value
  const dy = t.clientY - lastY.value
  const { limitX, limitY } = getLimits()
  const nextX = translateX.value + dx
  const nextY = translateY.value + dy
  translateX.value = applyRubber(nextX, limitX)
  translateY.value = applyRubber(nextY, limitY)
  lastX.value = t.clientX
  lastY.value = t.clientY
}
const onTouchEnd = (e: TouchEvent) => {
  if (e.touches && e.touches.length < 2 && e.changedTouches && e.changedTouches.length < 2) {
    pinchStartDistance.value = 0
  }
  if (!e.touches || e.touches.length === 0) {
    dragging.value = false
    const { limitX, limitY } = getLimits()
    const targetX = Math.max(-limitX, Math.min(limitX, translateX.value))
    const targetY = Math.max(-limitY, Math.min(limitY, translateY.value))
    if (targetX !== translateX.value || targetY !== translateY.value) {
      transitioning.value = true
      translateX.value = targetX
      translateY.value = targetY
      window.setTimeout(() => {
        transitioning.value = false
      }, 200)
    }
  }
}
const onWheel = (e: WheelEvent) => {
  e.preventDefault()
  const step = e.deltaY > 0 ? -0.1 : 0.1
  const next = Math.max(minScale.value, Math.min(maxScale.value, scale.value + step))
  zoomAt(e.clientX, e.clientY, next)
}
</script>

<style scoped lang="scss">
.viewer-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
}
.viewer-image {
  max-width: 100%;
  max-height: 100%;
  user-select: none;
  cursor: grab;
  transform-origin: center center;
}
.viewer-image:active {
  cursor: grabbing;
}
.viewer-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
}
.viewer-close {
  cursor: pointer;
  font-size: 25px;
}
.wm-overlay-in-viewer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  background-repeat: repeat;
  background-size: 260px 260px;
}
</style>
